import React, { useEffect, useState } from 'react';
import { Box, Button, Switch, TextField, TablePagination, FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle, DialogContent, Typography, DialogActions } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import PageHeader1 from '../../components/common/PageHeader1';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';

const API_URL = `${process.env.REACT_APP_BASEURL}/admin/cashBack`;
const TOGGLE_URL = `${process.env.REACT_APP_BASEURL}/admin/toggleCashBack`;
const AUTH_TOKEN = localStorage.getItem('astroVani-admin-token');

const sessionTypeOptions = [
    { value: 'chat', label: 'Chat' },
    { value: 'call', label: 'Call' },
    { value: 'videoCall', label: 'Video Call' },
];

const typeOptions = [
    { value: 'fixed', label: 'Fixed' },
    { value: 'percentage', label: 'Percentage' },
];

const formatTimeForDisplay = (time) => {
    if (!time || typeof time !== 'string') return '-';

    const [hours = '', minutes = ''] = time.split(':');

    if (hours === '' || minutes === '') return '-';

    const normalizedHours = Number(hours);
    const normalizedMinutes = Number(minutes);

    if (
        Number.isNaN(normalizedHours) ||
        Number.isNaN(normalizedMinutes) ||
        normalizedHours < 0 ||
        normalizedHours > 23 ||
        normalizedMinutes < 0 ||
        normalizedMinutes > 59
    ) {
        return '-';
    }

    const meridiem = normalizedHours >= 12 ? 'PM' : 'AM';
    const hour12 = normalizedHours % 12 || 12;

    return `${String(hour12).padStart(2, '0')}:${String(normalizedMinutes).padStart(2, '0')} ${meridiem}`;
};

const formatDateForDisplay = (value) => {
    if (!value || typeof value !== 'string') return '-';

    const datePart = value.split('T')[0];

    return /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? datePart : '-';
};

function CashBackList() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [filters, setFilters] = useState({ sessionType: '', type: '', startDate: '', endDate: '' });
    const [pendingFilters, setPendingFilters] = useState({ sessionType: '', type: '', startDate: '', endDate: '' });
    const [openConfirm, setOpenConfirm] = useState(false);
    const [deleteId, setDeleteId] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        let url = `${API_URL}?page=${page + 1}&limit=${pageSize}`;
        if (filters.sessionType) url += `&sessionType=${filters.sessionType}`;
        if (filters.type) url += `&type=${filters.type}`;
        if (filters.search) url += `&search=${filters.search}`;
        if (filters.startDate) url += `&startDate=${filters.startDate}`;
        if (filters.endDate) url += `&endDate=${filters.endDate}`;
        const res = await fetch(url, {
            headers: { Authorization: AUTH_TOKEN },
        });
        const data = await res.json();
        setRows(
            (data.data || []).map((row, index) => ({
                ...row,
                serialNumber: (page * pageSize) + index + 1,
            }))
        );
        setTotal(data.paginationDetail?.totalDocs || 0);
        setLoading(false);
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line
    }, [page, pageSize, filters]);



    async function exportToCSV(currentPageData) {
        // Fetch all data from API for export
        let url = `${API_URL}`;
        // Optionally add filters if you want to export filtered data
        if (filters.sessionType) url += `&sessionType=${filters.sessionType}`;
        if (filters.type) url += `&type=${filters.type}`;
        if (filters.search) url += `&search=${filters.search}`;
        if (filters.startDate) url += `&startDate=${filters.startDate}`;
        if (filters?.startTime) url += `&startTime=${filters.startTime}`;
        if (filters.endDate) url += `&endDate=${filters.endDate}`;
        if (filters?.endTime) url += `&endTime=${filters.endTime}`;
        const res = await fetch(url, {
            headers: { Authorization: AUTH_TOKEN },
        });
        const data = await res.json();
        const allRows = (data.data || currentPageData || []).map((row) => ({
            ...row,
            startTime: formatTimeForDisplay(row.startTime),
            endTime: formatTimeForDisplay(row.endTime),
        }));
        if (!allRows.length) return;
        const replacer = (key, value) => value === null ? '' : value;
        const header = Object.keys(allRows[0]);
        const csv = [
            header.join(','),
            ...allRows.map(row => header.map(fieldName => JSON.stringify(row[fieldName], replacer)).join(','))
        ].join('\r\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const urlBlob = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = urlBlob;
        a.download = 'cashback-list.csv';
        a.click();
        window.URL.revokeObjectURL(urlBlob);
    }


    const handleToggle = async (id, isActive) => {
        await fetch(`${TOGGLE_URL}/${id}`, {
            method: 'PATCH',
            headers: {
                Authorization: AUTH_TOKEN,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ isActive }),
        });
        fetchData();
    };

    const confirmClose = () => {
        fetchData();
        setOpenConfirm(false);
        setDeleteId(null);
    }

    const couponDelete = async (id) => {
        try {
            let url = `${API_URL}/${id}`;
            const res = await fetch(url, {
                method: 'DELETE',
                headers: { Authorization: AUTH_TOKEN },
            });
            if (!res.ok) {
                throw new Error('Error deleting coupon');
            }
            confirmClose();
            toast.success('Cashback deleted successfully');
        } catch (error) {
            confirmClose();
            toast.error('Failed to delete cashback');
        }
    }

    const columns = [
        {
            field: 'serialNumber',
            headerName: 'S.No.',
            width: 90,
            sortable: false,
            filterable: false,
        },
        { field: 'type', headerName: 'Type', width: 120 },
        { field: 'value', headerName: 'Value', width: 100 },
        { field: 'sessionType', headerName: 'Session Type', width: 130 },
        {
            field: 'validFrom',
            headerName: 'Valid From',
            width: 140,
            renderCell: (params) => formatDateForDisplay(params.row?.validFrom)
        },
        {
            field: 'validTo',
            headerName: 'Valid To',
            width: 140,
            renderCell: (params) => formatDateForDisplay(params.row?.validTo)
        },
        {
            field: 'startTime',
            headerName: 'Start Time',
            width: 140,
            renderCell: (params) => formatTimeForDisplay(params.row?.startTime)
        },
        {
            field: 'endTime',
            headerName: 'End Time',
            width: 140,
            renderCell: (params) => formatTimeForDisplay(params.row?.endTime)
        },
        { field: 'maxUsesPerUser', headerName: 'Max Uses/User', width: 130 },
        {
            field: 'isActive',
            headerName: 'Active',
            width: 100,
            renderCell: (params) => (
                <Switch
                    checked={params.value}
                    onChange={e => handleToggle(params.row._id, e.target.checked)}
                    color="primary"
                />
            ),
        },
        // {
        //     field: 'actions',
        //     headerName: 'Actions',
        //     width: 120,
        //     renderCell: (params) => (
        //         <>
        //             <Button variant="outlined" size="small" onClick={() => navigate(`/cashback-edit/${params.row._id}`)}>
        //                 Edit
        //             </Button>
        //             <Button variant="outlined" size="small" onClick={() => { setOpenConfirm(true); setDeleteId(params.row._id) }} color="error" sx={{ ml: 1 }}>
        //                 Delete
        //             </Button>
        //         </>
        //     ),
        // },
    ];

    return (
        <Box>
            <PageHeader1 pagetitle="Cash Back List" righttitle="Add Cash Back" link="/cashback-add" routebutton={true} />
            <Box
                mb={2}
                display="flex"
                flexWrap="wrap"
                gap={2}
                alignItems="center"
            >
                {/* Session Type */}
                <FormControl
                    size="small"
                    sx={{
                        minWidth: 120,
                        flex: { xs: "1 1 100%", sm: "1 1 45%", md: "1 1 160px" }
                    }}
                >
                    <InputLabel>Session Type</InputLabel>
                    <Select
                        value={pendingFilters.sessionType}
                        label="Session Type"
                        onChange={e =>
                            setPendingFilters(f => ({ ...f, sessionType: e.target.value }))
                        }
                    >
                        <MenuItem value="">All</MenuItem>
                        {sessionTypeOptions.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Type */}
                <FormControl
                    size="small"
                    sx={{
                        minWidth: 150,
                        flex: { xs: "1 1 100%", sm: "1 1 45%", md: "1 1 180px" }
                    }}
                >
                    <InputLabel>Type</InputLabel>
                    <Select
                        value={pendingFilters.type}
                        label="Type"
                        onChange={e => setPendingFilters(f => ({ ...f, type: e.target.value }))}
                    >
                        <MenuItem value="">All</MenuItem>
                        {typeOptions.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* From Date */}
                <TextField
                    size="small"
                    type="date"
                    label="From"
                    InputLabelProps={{ shrink: true }}
                    value={pendingFilters.startDate}
                    onChange={e =>
                        setPendingFilters(f => ({ ...f, startDate: e.target.value }))
                    }
                    sx={{
                        flex: { xs: "1 1 48%", sm: "1 1 45%", md: "1 1 140px" }
                    }}
                />

                {/* To Date */}
                <TextField
                    size="small"
                    type="date"
                    label="To"
                    InputLabelProps={{ shrink: true }}
                    value={pendingFilters.endDate}
                    onChange={e =>
                        setPendingFilters(f => ({ ...f, endDate: e.target.value }))
                    }
                    sx={{
                        flex: { xs: "1 1 48%", sm: "1 1 45%", md: "1 1 140px" }
                    }}
                />
            </Box>
            <Box mb={2} display="flex" justifyContent="flex-end" gap={2}>
                {/* Apply Button */}
                <Button
                    variant="contained"
                    color="primary"
                    size="medium"
                    sx={{ minWidth: 90, fontWeight: 600 }}
                    onClick={() => {
                        setFilters({ ...pendingFilters });
                        setPage(0);
                    }}
                    disabled={
                        !pendingFilters.sessionType &&
                        !pendingFilters.type &&
                        !pendingFilters.startDate &&
                        !pendingFilters.endDate
                    }
                >
                    Apply
                </Button>

                {/* Reset Button */}
                <Button
                    variant="outlined"
                    color="secondary"
                    size="medium"
                    sx={{ minWidth: 90, fontWeight: 600 }}
                    onClick={() => {
                        setPendingFilters({ sessionType: '', type: '', startDate: '', endDate: '' });
                        setFilters({ sessionType: '', type: '', startDate: '', endDate: '' });
                        setPage(0);
                    }}
                    disabled={
                        !pendingFilters.sessionType &&
                        !pendingFilters.type &&
                        !pendingFilters.startDate &&
                        !pendingFilters.endDate
                    }
                >
                    Reset
                </Button>
                <Button variant="outlined" color="success" size="medium" sx={{ minWidth: 110, fontWeight: 600 }} onClick={() => exportToCSV(rows)}>Export</Button>
            </Box>
            <DataGrid
                autoHeight
                rows={rows}
                columns={columns}
                rowCount={total}
                pagination
                paginationMode="server"
                paginationModel={{ page, pageSize }}
                onPaginationModelChange={(model) => {
                    setPage(model?.page);
                    setPageSize(model?.pageSize);
                }}
                loading={loading}
                getRowId={(row) => row._id}
            />
            <Dialog open={openConfirm} onClose={() => confirmClose()}>
                <DialogTitle>Confirm Overlap</DialogTitle>
                <DialogContent>
                    <Typography>
                        An active cashback already exists for the selected duration.
                        Do you want to continue?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => confirmClose()}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={() => {
                        couponDelete(deleteId);
                    }}>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}


export default CashBackList;
